import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Col, Row, message, Typography, Skeleton, Table, notification } from "antd";

import { getGapsDeleted, getGapsDeletedByCode } from "../../../../API/gaps";

import WithPermission from "../../../Common/WithPermission/WithPermission";
import ExportButton from '../../../Common/ExportButton/ExportButton';
import BackButton from "../../../Common/BackButton/BackButton";
import BreadCrumb from "../../../Common/BreadCrumb/BreadCrumb";
import PageTitle from "../../../Common/PageTitle/PageTitle";
import ChangeLog from "../../../Common/ChangeLog/ChangeLog";
import GAPsSearch from "../GAPsSearch/GAPsSearch";
import Columns from "./columns"

import { useAuth } from "../../../Context/AuthContext";

const GapsDeleted = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  // Permisos
  const { userData } = useAuth();
  // Consulta permisos: Superusuario  
  const isSuperuser = userData?.is_superuser === true;
  
  // Obtener los filtros del Dashboard de la URL
  const assetsFromUrl = searchParams.get('assets')?.split(',').filter(Boolean) || [];
  const areasFromUrl = searchParams.get('areas')?.split(',').filter(Boolean) || [];
  
  // Notificaciones
  const [api, contextHolder] = notification.useNotification();
  // Data
  const [gapsDeleted, setGapsDeleted] = useState(null);
  // Buscador
  const [filteredData, setFilteredData] = useState([]); 
  const [searchText, setSearchText] = useState(''); 

  useEffect(() => {
    const fetchGapsDeleted = async () => {
      try {
        const data = await getGapsDeleted();
        setGapsDeleted(data);
        setFilteredData(data);
      } catch (error) {
        message.error("No se encontraron los gaps eliminados.");
      }
    };

    fetchGapsDeleted();
  }, []);

  // Construir parámetros para mantener filtros al volver
  const filterParams = new URLSearchParams();
  if (assetsFromUrl.length) {
    filterParams.set('assets', assetsFromUrl.join(','));
  }
  if (areasFromUrl.length) {
    filterParams.set('areas', areasFromUrl.join(','));
  }
  
  const breadCrumbUrl = [
    { 
      label: "Gaps", 
      url: "/gaps",
      search: filterParams.toString() ? `?${filterParams.toString()}` : ''
    },
    { label: "Eliminados" },
  ];

  const goToBioFunctions = () => {
    navigate(-1);
  };

  const [changeLogVisible, setChangeLogVisible] = useState(false);
  const [changeLogData, setChangeLogData] = useState(null);
  const [changeLogLogs, setChangeLogLogs] = useState([]);

  const handleOpenChangeLog = async (record) => {
    try {
      const gaps = await getGapsDeletedByCode(record.code);
      setChangeLogData(record);
      setChangeLogLogs(gaps.logs || []);
      setChangeLogVisible(true); 
    } catch (error) {
      api.error({ message: "Error al obtener logs", description: error.message });
    }
  };

  const handleCloseChangeLog = () => {
    setChangeLogVisible(false);
    setTimeout(() => {
      setChangeLogData(null);
      setChangeLogLogs([]);
    }, 300);
  };

  const gapsDeletedTableFilter = () => {
    const gapAssets = Array.from(
      new Map(
        gapsDeleted.map(gap => [
          gap.dependency_control?.dependency?.asset?.name, 
          gap.dependency_control?.dependency?.asset?.name
        ]).filter(([name]) => name)
      ).values()
    );

    const gapDependencies = Array.from(
      new Map(
        gapsDeleted.map(gap => [
          gap.dependency_control?.dependency?.name, 
          gap.dependency_control?.dependency?.name
        ]).filter(([name]) => name)
      ).values()
    );

    const gapPriorities = Array.from(
      new Map(
        gapsDeleted
          .map(gap => [
            gap.dependency_control?.control?.priority?.name,
            gap.dependency_control?.control?.priority
          ])
          .filter(([name]) => name)
      ).values()
    );

    const gapTypes = Array.from(
      new Map(
        gapsDeleted.map(gap => [gap.type, gap.type]).filter(([type]) => type)
      ).values()
    );

    const gapStatus = Array.from(
      new Map(
        gapsDeleted.map(gap => [gap.status, gap.status]).filter(([status]) => status)
      ).values()
    );

    const gapArea = Array.from(
      new Map(
        gapsDeleted
          .map(gap => [gap.area?.name, gap.area])
          .filter(([name]) => name)
      ).values()
    );

    const gapIssuesInProgress = Array.from(
      new Map(
        gapsDeleted.map(gap => [gap.issue_in_progress, gap.issue_in_progress]).filter(([issue]) => issue)
      ).values()
    );

    const gapIssuesFinished = Array.from(
      new Map(
        gapsDeleted
          .flatMap(gap => (gap.issues_finished || []).map(issue => [issue, issue]))
          .filter(([issue]) => issue)
      ).values()
    );

    return {
      assets: gapAssets.map(name => ({ name })),
      dependencies: gapDependencies.map(name => ({ name })),
      types: gapTypes.map(name => ({ name })),
      statuses: gapStatus.map(name => ({ name })),
      areas: gapArea.map(area => ({ name: area.name })),
      priorities: gapPriorities.map(priority => ({ name: priority.name })),
      issuesInProgress: gapIssuesInProgress.map(name => ({ name })),
      issuesFinished: gapIssuesFinished.map(name => ({ name })),
      dashboardAssetsFilter: assetsFromUrl ?? [],
      dashboardAreasFilter: areasFromUrl ?? [],
    };
  };

  return (
    <WithPermission
      isSuperuser={isSuperuser}
      requiredPermission="read"
      redirectTo="/home"
    >
      <>
        {contextHolder}
        <div className="button-container">
          <Row justify="space-between">
            <Col span={18}>
              <PageTitle title="GAPs Eliminados" icon="gaps" />
            </Col>
          </Row>

          <Row justify="start" align="top">
            <Col span={18} style={{ display: "flex", alignItems: "center" }}>
              <BackButton onConfirm={goToBioFunctions} />
              <BreadCrumb location={breadCrumbUrl} />
            </Col>
            <Col span={6} style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <GAPsSearch 
                gapsData={gapsDeleted}
                searchText={searchText}
                setSearchText={setSearchText}
                setFilteredData={setFilteredData}
              />
            </Col>
          </Row>

          <Row justify="end">
            <Col span={6} offset={18} style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <ExportButton
                api={api}
                contextHolder={contextHolder}
                data={filteredData}
                filename="Gaps Eliminados.csv"
                transformData={(data) =>
                  data.map(item => ({
                    Activo: item.dependency_control?.dependency?.asset?.name || 'N/A',
                    Dependencia: item.dependency_control?.dependency?.name || 'N/A',
                    Control: item.dependency_control?.control?.title || 'N/A',
                    'Tipo de GAP': item.type || 'N/A',
                    'Estado del GAP': item.status || 'N/A',
                    'Issue en curso': item.issue_in_progress || '-',
                    'Issues finalizados': (item.issues_finished || []).join(', ') || '-',
                    'Responsable': item.area?.name || 'N/A'
                  }))
                }
              />
            </Col>
          </Row>
        </div>
      {
        !gapsDeleted ? (
        <>
          <Skeleton active paragraph={{ rows: 6 }} />
          <div style={{ height: 32 }} />
          <Row gutter={[16, 16]}>
            <Col xs={24} md={12}>
              <Skeleton active paragraph={{ rows: 6 }} />
            </Col>
            <Col xs={24} md={12}>
              <Skeleton active paragraph={{ rows: 6 }} />
            </Col>
          </Row>
        </>
        ) : (
          <>
            <Table
              className="custom-pagination deleted-table "
              columns={Columns(handleOpenChangeLog, gapsDeletedTableFilter())}
              dataSource={filteredData} 
              rowKey="code"
              scroll={{ y: 450 }}
              pagination={{
                defaultPageSize: 30,
                showSizeChanger: true,
                pageSizeOptions: ["30", "50", "100"],
                showTotal: (total, range) =>
                  `${range[0]}-${range[1]} de ${total} registros`,
              }}
            />

            <ChangeLog
              open={changeLogVisible}
              onClose={handleCloseChangeLog}
              data={changeLogData}
              logs={changeLogLogs}
            />
          </>
        )
      }
      </>
    </WithPermission>
  );
};

export default GapsDeleted;